import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Page,
  LegacyCard,
  IndexTable,
  Badge,
  Text,
  TextField,
  Pagination,
  Banner,
  EmptyState,
  Button,
  Tooltip,
  InlineStack,
  BlockStack,
  Box,
} from "@shopify/polaris";
import {
  SearchIcon,
  ExternalIcon,
  EmailIcon,
  ArrowRightIcon,
} from "@shopify/polaris-icons";
import { useAppBridge } from "../components/createContext/AppBridgeContext";
import { getAppBridgeToken } from "../utils/getAppBridgeToken";
import { TablePageSkeleton } from "../components/loading/PageSkeletons";

const PAGE_SIZE = 25;

// Columns the table can sort on, in heading order. `null` = not sortable.
// Must match the backend's SORTABLE whitelist.
const SORT_FIELDS = [
  "installedAt", // S.N. maps to install order
  "shopName",
  null, // Shop URL
  null, // Email
  null, // Phone
  null, // Status
  null, // Plan
  "installedAt",
  null, // Updated at
  null, // Action
];

const pad = (n) => String(n).padStart(2, "0");

// Reference format: date on the first line, clock time under it.
const splitDate = (value) => {
  if (!value) return { date: "—", time: "" };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: "—", time: "" };
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`,
  };
};

const planTone = (plan) => {
  if (plan.inTrial) return "attention";
  switch (String(plan.status).toLowerCase()) {
    case "active":
      return "success";
    case "trial":
      return "attention";
    case "expired":
    case "cancelled":
    case "uninstalled":
      return "critical";
    case "paused":
      return "warning";
    default:
      return "info";
  }
};

const planLabel = (plan) => (plan.inTrial ? `${plan.name} · Trial` : plan.name);

const storeHandle = (domain) => String(domain || "").split(".")[0];

const DateCell = ({ value }) => {
  const { date, time } = splitDate(value);
  return (
    <BlockStack gap="050">
      <Text as="span" variant="bodySm">
        {date}
      </Text>
      {time ? (
        <Text as="span" variant="bodySm" tone="subdued">
          {time}
        </Text>
      ) : null}
    </BlockStack>
  );
};

export default function SuperAdminArea() {
  const app = useAppBridge();
  const [shops, setShops] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  // Index 7 = "Installed At", the default sort column.
  const [sortIndex, setSortIndex] = useState(7);
  const [sortDirection, setSortDirection] = useState("descending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = await getAppBridgeToken(app);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_HOST}/super-admin/shops`,
        {
          params: {
            page,
            limit: PAGE_SIZE,
            search: query || undefined,
            sort: SORT_FIELDS[sortIndex] || undefined,
            order: sortDirection === "ascending" ? "asc" : "desc",
          },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setShops(res.data?.data || []);
      setPagination(res.data?.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      const status = err?.response?.status;
      setShops([]);
      setError(
        status === 403
          ? "You are not authorized to view this area."
          : err?.response?.data?.message || "Failed to load shops.",
      );
    } finally {
      setLoading(false);
    }
  }, [app, page, query, sortIndex, sortDirection]);

  useEffect(() => {
    load();
  }, [load]);

  // Debounce search input into the query that actually hits the API.
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const handleSort = useCallback((index, direction) => {
    if (!SORT_FIELDS[index]) return;
    setSortIndex(index);
    setSortDirection(direction);
    setPage(1);
  }, []);

  const rows = shops.map((s, index) => {
    const rowIndex = (pagination.page - 1) * PAGE_SIZE + index + 1;
    const domain = s.myshopifyDomain || s.shop;
    const handle = storeHandle(domain);

    return (
      <IndexTable.Row id={String(s._id)} key={String(s._id)} position={index}>
        <IndexTable.Cell>
          <Text as="span" variant="bodySm" tone="subdued">
            {rowIndex}
          </Text>
        </IndexTable.Cell>

        <IndexTable.Cell>
          <BlockStack gap="050">
            <Text as="span" variant="bodySm" fontWeight="semibold">
              {s.shopName || handle}
            </Text>
        
          </BlockStack>
        </IndexTable.Cell>

        <IndexTable.Cell>
          {domain ? (
            <Tooltip content={`Open ${domain}`}>
              <Button
                icon={ExternalIcon}
                variant="tertiary"
                size="slim"
                url={`https://${domain}`}
                target="_blank"
                accessibilityLabel={`Open storefront ${domain}`}
              />
            </Tooltip>
          ) : (
            <Text as="span" variant="bodySm" tone="subdued">
              —
            </Text>
          )}
        </IndexTable.Cell>

        <IndexTable.Cell>
          {s.email ? (
            <InlineStack gap="100" blockAlign="center" wrap={false}>
              <Text as="span" variant="bodySm">
                {s.email}
              </Text>
              <Tooltip content={`Email ${s.email}`}>
                <Button
                  icon={EmailIcon}
                  variant="tertiary"
                  size="slim"
                  url={`mailto:${s.email}`}
                  accessibilityLabel={`Email ${s.email}`}
                />
              </Tooltip>
            </InlineStack>
          ) : (
            <Text as="span" variant="bodySm" tone="subdued">
              —
            </Text>
          )}
        </IndexTable.Cell>

        <IndexTable.Cell>
          <Text as="span" variant="bodySm" tone="subdued">
            {s.phone || "—"}
          </Text>
        </IndexTable.Cell>

        <IndexTable.Cell>
          <BlockStack gap="050">
            <Badge tone={s.installed ? "success" : undefined}>
              {s.installed ? "Installed" : "Not Installed"}
            </Badge>
            <Text as="span" variant="bodySm" tone="subdued">
              {s.appEnabled ? "App on" : "App off"}
            </Text>
          </BlockStack>
        </IndexTable.Cell>


        <IndexTable.Cell>
          <Badge tone={planTone(s.plan)}>{planLabel(s.plan)}</Badge>
        </IndexTable.Cell>

        <IndexTable.Cell>
          <DateCell value={s.installedAt} />
        </IndexTable.Cell>

        <IndexTable.Cell>
          <DateCell value={s.updatedAt} />
        </IndexTable.Cell>

        <IndexTable.Cell>
          {handle ? (
            <Tooltip content="Open in Shopify admin">
              <Button
                icon={ArrowRightIcon}
                variant="tertiary"
                size="slim"
                url={`https://admin.shopify.com/store/${handle}`}
                target="_blank"
                accessibilityLabel={`Open ${handle} in Shopify admin`}
              />
            </Tooltip>
          ) : (
            <Text as="span" variant="bodySm" tone="subdued">
              —
            </Text>
          )}
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  return (
    <Page
      fullWidth
      title="Shop Installations"
      subtitle={
        pagination.total
          ? `${pagination.total} shop${pagination.total === 1 ? "" : "s"}`
          : undefined
      }
    >
      <LegacyCard>
        <Box padding="400" paddingBlockEnd="200">
          <TextField
            label="Search shops"
            labelHidden
            placeholder="Search by store, domain, owner or email"
            value={search}
            onChange={setSearch}
            prefix={<SearchIcon />}
            autoComplete="off"
            clearButton
            onClearButtonClick={() => setSearch("")}
          />
        </Box>

        {error ? (
          <Box padding="400">
            <Banner tone="critical" title={error} />
          </Box>
        ) : loading ? (
          <TablePageSkeleton />
        ) : shops.length === 0 ? (
          <EmptyState
            heading={query ? "No shops match your search" : "No shops installed yet"}
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>
              {query
                ? "Try a different store name, domain or email."
                : "Shops will appear here as merchants install the app."}
            </p>
          </EmptyState>
        ) : (
          <IndexTable
            resourceName={{ singular: "shop", plural: "shops" }}
            itemCount={shops.length}
            selectable={false}
            sortable={SORT_FIELDS.map(Boolean)}
            sortColumnIndex={sortIndex}
            sortDirection={sortDirection}
            onSort={handleSort}
            headings={[
              { title: "S.N." },
              { title: "Shop Name" },
              { title: "Shop URL" },
              { title: "Email" },
              { title: "Phone" },
              { title: "Status" },
              { title: "Plan" },
              { title: "Installed At" },
              { title: "Updated At" },
              { title: "Action" },
            ]}
          >
            {rows}
          </IndexTable>
        )}

        {!error && pagination.pages > 1 ? (
          <Box padding="400">
            <InlineStack align="center">
              <Pagination
                hasPrevious={page > 1}
                onPrevious={() => setPage((p) => Math.max(1, p - 1))}
                hasNext={page < pagination.pages}
                onNext={() => setPage((p) => p + 1)}
                label={`Page ${pagination.page} of ${pagination.pages}`}
              />
            </InlineStack>
          </Box>
        ) : null}
      </LegacyCard>
    </Page>
  );
}
